import { createApi } from '@reduxjs/toolkit/query/react';
import { gql } from 'graphql-request';
import { baseQuery } from '@classity-edtech/ui-base';
import { AUTHENTICATION } from '../store/actionTypes';
import { User } from './models/profile';
import { TokenInput } from './models/authentication';
import { Token } from 'graphql';

export const authentication = createApi({
  reducerPath: AUTHENTICATION,
  baseQuery,
  tagTypes: ['Authentication'],
  endpoints: builder => ({
    signUpEmail: builder.mutation<User, { email: string; password: string }>({
      query: variables => ({
        document: gql`
          mutation Signup($userInput: UserInput!) {
            signup(userInput: $userInput) {
              id
              email
            }
          }
        `,
        variables: { userInput: variables },
      }),
      transformResponse: (response: any) => response?.signup,
    }),
    login: builder.mutation<string, { email: string; password: string }>({
      query: variables => ({
        document: gql`
          mutation Login($userInput: UserSignInInput!) {
            login(userInput: $userInput)
          }
        `,
        variables: { userInput: variables },
      }),
      transformResponse: (response: any) => response?.login,
    }),
    logout: builder.mutation<boolean, { clientId: string }>({
      query: variables => ({
        document: gql`
          mutation Logout($clientId: String!) {
            logout(clientId: $clientId)
          }
        `,
        variables,
      }),
      transformResponse: (response: any) => response?.logout,
    }),
    verifyPhone: builder.mutation<{ phone: string }, { phone: string }>({
      query: variables => ({
        document: gql`
          mutation VerifyPhone($phone: String) {
            verifyPhone(phone: $phone) {
              phone
            }
          }
        `,
        variables,
      }),
      transformResponse: (response: any) => response?.verifyPhone,
    }),
    verifyToken: builder.mutation<Token, TokenInput>({
      query: variables => ({
        document: gql`
          mutation VerifyToken($tokenInput: VerifyTokenInput!) {
            verifyToken(tokenInput: $tokenInput) {
              role
              user
              token
            }
          }
        `,
        variables,
      }),
      transformResponse: (response: any) => response?.verifyToken,
    }),
    emailVerification: builder.mutation<{ email: string }, { email: string }>({
      query: variables => ({
        document: gql`
          mutation VerifyEmail($email: String) {
            verifyEmail(email: $email) {
              email
            }
          }
        `,
        variables,
      }),
      transformResponse: (response: any) => response?.verifyEmail,
    }),
    verifyEmail: builder.query<{ email: string }, { link: string }>({
      query: variables => ({
        document: gql`
          query OneTimePasscode($link: String!) {
            oneTimePasscode(link: $link) {
              email
            }
          }
        `,
        variables,
      }),
      transformResponse: (response: any) => response?.oneTimePasscode,
    }),
    forgotPassword: builder.mutation<{ email: string }, { email: string }>({
      query: variables => ({
        document: gql`
          query ForgotPassword($email: String) {
            forgotPassword(email: $email) {
              email
            }
          }
        `,
        variables,
      }),
      transformResponse: (response: any) => response?.forgotPassword,
    }),
    resetPassword: builder.mutation<User, { emailToken: string; password: string }>({
      query: variables => ({
        document: gql`
          mutation ResetPassword($resetPasswordInput: ResetPasswordInput!) {
            resetPassword(resetPasswordInput: $resetPasswordInput) {
              email
            }
          }
        `,
        variables: { resetPasswordInput: variables },
      }),
      transformResponse: (response: any) => response?.resetPassword,
    }),
    googleSignIn: builder.mutation<string, { token: string; clientId: string; role: string }>({
      query: variables => ({
        document: gql`
          mutation GoogleSignIn($googleAuthInput: GoogleSignInInput!) {
            googleSignIn(googleAuthInput: $googleAuthInput)
          }
        `,
        variables: { googleAuthInput: variables },
      }),
      transformResponse: (response: any) => response?.googleSignIn,
    }),
    getGeoLocation: builder.query<any, void>({
      query: () => ({
        document: gql`
          query Query {
            geoLocation
          }
        `,
      }),
      transformResponse: (response: any) => response?.getGeoLocationByIpAddress,
    }),
  }),
});

export const {
  useSignUpEmailMutation,
  useVerifyPhoneMutation,
  useLoginMutation,
  useLazyVerifyEmailQuery,
  useVerifyTokenMutation,
  useEmailVerificationMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useGetGeoLocationQuery,
  useGoogleSignInMutation,
  useLogoutMutation,
} = authentication;
